/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getSeguridadSocialAsignacion,
  getSeguridadSocialAsignaciones,
  patchSeguridadSocialAsignacion,
  postSeguridadSocialAsignacion,
  deleteSeguridadSocialAsignacion,
  autorizarSeguridadSocialAsignacion,
} from 'api/nominas/seguridadSocialAsig';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { render } from '@testing-library/react';
import moment from 'moment';
import { getCatalogosDeModalidadDeSeguridadSocial } from 'api/catalogos/ModalidadDeSeguridadSocial';

const SeguridadSocialAsig = ({ permission }) => {
  const [form] = Form.useForm();
  const [isAdding, setAdding] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [modalidadSeguridadSocial, setModalidadSeguridadSocial] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getSeguridadSocialAsignaciones();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _ModalidadSeguridadSocial,
        _estados,
        _data,
      ] = await Promise.all([
        getCatalogosDeModalidadDeSeguridadSocial(),
        getEstadosGlobales(),
        getSeguridadSocialAsignaciones(),
      ]);
      if (mounted) {
        const modalidadSeguridadSocialAdapted = _ModalidadSeguridadSocial
          .filter((e) => e.modalidad && e.modalidad.trim() !== '') // keep only non-empty modalidad
          .map((modSegSocial) => {
            const _seguridadSocial = _ModalidadSeguridadSocial.find(
              (s) => s.id === modSegSocial.seguridad_social_padre,
            );
            return {
              id: modSegSocial.id,
              descripcion: `${_seguridadSocial.seguridad_social} - ${modSegSocial.modalidad}`,
            };
          });
        setModalidadSeguridadSocial(modalidadSeguridadSocialAdapted);
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    setAdding(false);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _nivelDeEstudio = await getSeguridadSocialAsignacion(match.id);
    setTimeout(() => {
      if (_nivelDeEstudio) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_nivelDeEstudio));
          setDisableFields(_nivelDeEstudio?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };
  const onClickAdd = () => {
    setAdding(true);
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const valuesForm = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len

      const values = {
        ...formatReceived(valuesForm),
        fecha_inicial: valuesForm.fecha_inicial
          ? valuesForm.fecha_inicial.format('YYYY-MM-DD')
          : null,
        fecha_final: valuesForm.fecha_final
          ? valuesForm.fecha_final.format('YYYY-MM-DD')
          : null,
      };
      const saved = await (key
        ? patchSeguridadSocialAsignacion(key, values)
        : postSeguridadSocialAsignacion(values)
      );
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteSeguridadSocialAsignacion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar la Asignación de Seguridad Social ya autorizada');
        return;
      }
      const saved = await autorizarSeguridadSocialAsignacion(key);
      if (saved) {
        const _data = await getSeguridadSocialAsignaciones();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Seguridad Social',
      dataIndex: 'seguridad_social',
      key: 'seguridad_social',
      width: 400,
      render: (val) => modalidadSeguridadSocial.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 200,
      render: (val) => val?.format('YYYY-MM-DD'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 200,
      render: (val) => val?.format('YYYY-MM-DD'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estatus',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'asignaciondeseguridadsocial', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportParam={false}
          allowSearch={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Asignación de Seguridad Social`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="seguridad_social"
                rules={rules.required}
                label="Seguridad Social"
                hasFeedback
              >
                <Select
                  dataSource={modalidadSeguridadSocial}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_inicial"
                rules={rules.required}
                label="Fecha Inicial"
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="YYYY-MM-DD"
                  disabled={disableFields}
                  placeholder=""
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_final"
                label="Fecha Final"
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="YYYY-MM-DD"
                  disabled={isAdding || selectedData?.estados_globales === ESTATUS_INACTIVO}
                  placeholder=""
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Asignación de Seguridad Social ${modalidadSeguridadSocial.find((e) => e.id === selectedData?.seguridad_social)?.descripcion}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la Asignación de Seguridad Social ${modalidadSeguridadSocial.find((e) => e.id === selectedData?.seguridad_social)?.descripcion}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar la Asignación de Seguridad Social ${modalidadSeguridadSocial.find((e) => e.id === selectedData?.seguridad_social)?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default SeguridadSocialAsig;
